import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Calendar, Target, X, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { DueReflection, dismissReflectionPrompt, useDueReflections } from "@/hooks/useDueReflections";

interface ReflectionDuePromptProps {
  reflection: DueReflection;
  onDismiss?: () => void;
  showSeeAll?: boolean;
  compact?: boolean;
}

export function ReflectionDuePrompt({
  reflection,
  onDismiss,
  showSeeAll = true,
  compact = false,
}: ReflectionDuePromptProps) {
  const navigate = useNavigate();
  const { data: allDue } = useDueReflections();
  const dueCount = allDue?.length || 0;

  const handleComplete = () => {
    navigate(`/journey/summary/${reflection.id}?action=reflect`);
  };

  const handleDismiss = () => {
    dismissReflectionPrompt(reflection.id);
    onDismiss?.();
  };

  const handleSeeAll = () => {
    navigate("/journey/reflections-due");
  };

  if (compact) {
    return (
      <button
        onClick={handleComplete}
        className="w-full text-left p-3 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors group"
      >
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Calendar className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-small font-medium text-foreground truncate">
              Quick check-in due
            </p>
            <p className="text-caption text-muted-foreground truncate">
              {reflection.meetingTitle}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0 mt-2" />
        </div>
      </button>
    );
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-small font-medium text-foreground">
                Quick check-in due
              </p>
              <p className="text-caption text-muted-foreground">
                Complete to update your summary
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Meeting info */}
        <div className="space-y-1.5">
          <p className="text-body font-medium text-foreground">
            {reflection.meetingTitle}
          </p>
          <div className="flex items-center gap-3 text-caption text-muted-foreground">
            <span>{format(reflection.meetingStartTime, "MMM d · h:mm a")}</span>
            <span>·</span>
            <div className="flex items-center gap-1">
              <Target className="h-3 w-3" />
              <span>{reflection.behaviorName}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button onClick={handleComplete} size="sm" className="flex-1">
            Complete quick check-in
            <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
          {showSeeAll && dueCount > 1 && (
            <Button variant="outline" size="sm" onClick={handleSeeAll}>
              See all ({dueCount})
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface ReflectionDueListProps {
  reflections: DueReflection[];
  emptyMessage?: string;
  className?: string;
}

export function ReflectionDueList({
  reflections,
  emptyMessage = "You're up to date. No reflections are due right now.",
  className,
}: ReflectionDueListProps) {
  const navigate = useNavigate();

  if (reflections.length === 0) {
    return (
      <div className={cn("text-center py-6", className)}>
        <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-small text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {reflections.map((reflection) => (
        <button
          key={reflection.id}
          onClick={() => navigate(`/journey/summary/${reflection.id}?action=reflect`)}
          className="w-full text-left p-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-small font-medium text-foreground truncate">
                {reflection.meetingTitle}
              </p>
              <div className="flex items-center gap-2 text-caption text-muted-foreground">
                <span>{format(reflection.meetingStartTime, "MMM d · h:mm a")}</span>
                <span>·</span>
                <span className="truncate">{reflection.behaviorName}</span>
              </div>
            </div>
            <span className="shrink-0 rounded-md border border-input bg-background px-3 py-1 text-xs text-foreground">
              Open
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}
